// packages/storefront/src/translation/mountWidgetRoot.tsx
// `React` is in scope on purpose: `tsx --test` (mountWidgetRoot.test.ts) compiles this file with the root
// tsconfig's `jsx: preserve`, which it runs as the classic runtime (`React.createElement`). The embed's own build
// uses the automatic runtime.
import React, { StrictMode, type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import WidgetErrorBoundary from '@tourtask/storefront/components/general/WidgetErrorBoundary';
import { storefrontEnv } from '@tourtask/storefront/storefrontEnv';
import { ensureDomGuard } from './domGuard';
import {
	configureWidgetErrorReporting,
	reportWidgetError,
	type WidgetName,
	type WidgetTelemetry,
} from './reportWidgetError';

export type { WidgetName } from './reportWidgetError';

/**
 * Mounts an embed widget. The ONLY `createRoot` call site in the app — ESLint
 * forbids `react-dom/client` everywhere else in `apps/embed/src` — so every
 * root gets, by construction:
 *
 * 1. **The DOM guard** (`./domGuard`), installed before the root exists and
 *    told about the root right after `createRoot`, before the first render.
 *    It is what lets a browser page translator (Chrome, Edge, Safari, Firefox,
 *    the Google website widget) rewrite the widget's text without React's next
 *    commit throwing `NotFoundError` — the crash that took a live storefront
 *    down on 2026-09-22 (docs/embed-browser-translation.md).
 * 2. **React's root error callbacks and the guard's events**, reported through
 *    `reportWidgetError` (console always; the `/v1` telemetry sink when the
 *    root carries its business credentials).
 * 3. **`WidgetErrorBoundary`** around the whole tree, so a crash never leaves a
 *    blank rectangle (or, for `fallback: 'none'`, anything at all) on the
 *    partner's page.
 *
 * A root whose react-dom copy the guard cannot identify still renders; the
 * guard reports it once as `guard:no-react-key`.
 */
export function mountWidgetRoot(
	container: HTMLElement,
	app: ReactNode,
	options: {
		widget: WidgetName;
		fallback: 'notice' | 'none';
		/** From the root's data-business-id / data-business-token; null when absent (then only console). */
		telemetry: WidgetTelemetry | null;
	},
): Root {
	const { widget, fallback, telemetry } = options;
	configureWidgetErrorReporting(widget, telemetry);

	const reg = ensureDomGuard(window, { build: storefrontEnv().buildId });
	// Guard events are page-level: delivered once per kind per page view, to the
	// FIRST listener that does not throw — whichever widget bundle mounted first.
	reg?.impl?.onEvent((e) => reportWidgetError({ widget, kind: `guard:${e.kind}` }));

	const root = createRoot(container, {
		onCaughtError: (error, info) =>
			reportWidgetError({ widget, kind: 'caught', error, componentStack: info.componentStack }),
		onUncaughtError: (error, info) =>
			reportWidgetError({ widget, kind: 'uncaught', error, componentStack: info.componentStack }),
		onRecoverableError: (error, info) =>
			reportWidgetError({ widget, kind: 'recoverable', error, componentStack: info.componentStack }),
	});
	// After createRoot (the container now carries this react-dom copy's key),
	// before render (the first commit already creates guarded nodes).
	reg?.impl?.registerReactRoot(container);

	root.render(
		<StrictMode>
			<WidgetErrorBoundary widget={widget} fallback={fallback}>
				{app}
			</WidgetErrorBoundary>
		</StrictMode>,
	);
	return root;
}
